import classNames from "classnames";
import { ChevronDown } from "lucide-react";
import React, {
  Suspense,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { hasBlockingOverlay } from "@/helpers/focus";
import { useHotkey } from "@/hooks/useHotkey";
import { useIsTruncated } from "@/hooks/useIsTruncated";
import usePopupState from "@/hooks/usePopupState";
import EntityIconSlot from "./EntityIconSlot";
import { switcherTestId } from "./helpers";
import type { BreadcrumbSwitcherRef, EntityIcon } from "./types";

import "./entitySwitcher.scss";

const BLOCK = "tlx-breadcrumb-switcher";

/** Gap between the trigger and the panel, and the panel's minimum inset. */
const PANEL_GAP = 6;
const VIEWPORT_INSET = 8;

// Lazy so the descriptor registry — and with it the API client and the service
// icon catalogue — stays out of the layout chunk. `Bredcrumbs` renders on every
// page, but the popover body only ever loads if someone opens a switcher.
const EntitySwitcherPopover = React.lazy(
  () => import("./EntitySwitcherPopover"),
);

export interface BreadcrumbEntitySwitcherProps {
  switcher: BreadcrumbSwitcherRef;
  /** The crumb's own label. */
  label: string;
  /** The crumb's own icon. */
  icon?: EntityIcon;
  /** Keeps the Cypress `.breadcrumb-last-item-label` contract when last. */
  isLast: boolean;
  /** Only one switcher per page claims the `/` shortcut. */
  isPrimary: boolean;
}

/**
 * Turns a breadcrumb entity name into a GitHub-style switcher: click it to
 * search the entity's siblings, switch while keeping the current tab, or jump
 * straight to another tab of another entity.
 *
 * The panel is portaled to `document.body` and positioned here rather than
 * through Kendo's `Popup`. `Popup` measures the anchor once when it opens and
 * never re-measures, so on a page still settling its layout it lands in the
 * wrong place — and its default horizontal `flip` can throw a wide panel clean
 * off the left edge.
 */
const BreadcrumbEntitySwitcher: React.FC<BreadcrumbEntitySwitcherProps> = ({
  switcher,
  label,
  icon,
  isLast,
  isPrimary,
}) => {
  const { t } = useTranslation();
  const [isOpen, open, close] = usePopupState();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [labelRef, isLabelTruncated] = useIsTruncated<HTMLSpanElement>(label);
  const popoverId = `${useId()}-switcher`;
  const [position, setPosition] = useState<{ top: number; left: number }>();

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const anchor = trigger.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = panel;

    const left = Math.min(
      Math.max(anchor.left, VIEWPORT_INSET),
      Math.max(window.innerWidth - width - VIEWPORT_INSET, VIEWPORT_INSET),
    );

    const below = anchor.bottom + PANEL_GAP;
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_INSET;
    const top = fitsBelow
      ? below
      : Math.max(anchor.top - PANEL_GAP - height, VIEWPORT_INSET);

    setPosition(current =>
      current?.top === top && current?.left === left ? current : { top, left },
    );
  }, []);

  // Re-measured whenever the panel resizes (the lazy body replacing the
  // fallback, a filter shortening the list) and whenever the page moves under
  // it, so a layout that settles after opening cannot strand it.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(undefined);
      return;
    }
    reposition();

    // Coalesced to one measurement per frame: `reposition` forces two synchronous
    // layouts, and scroll fires far more often than that.
    let frame = 0;
    const scheduleReposition = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        reposition();
      });
    };

    // Capture, so a scroll inside any ancestor scroller is seen — but the panel
    // is `position: fixed`, so its own list scrolling cannot move it.
    const onScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      scheduleReposition();
    };

    const observer = new ResizeObserver(reposition);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener("resize", scheduleReposition);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleReposition);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen, reposition]);

  const openFromHotkey = useCallback(
    (event: KeyboardEvent) => {
      // Dialogs and slide panels run their own focus traps; do not yank focus
      // out from under one. Checked when the key is pressed rather than at
      // render time, because either can open without this component
      // re-rendering. Bailing before `preventDefault` leaves the key to the
      // browser rather than swallowing it for nothing.
      if (hasBlockingOverlay()) return;
      event.preventDefault();
      open();
    },
    [open],
  );

  useHotkey("/", openFromHotkey, { enabled: isPrimary && !isOpen });

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={classNames(BLOCK, { [`${BLOCK}--open`]: isOpen })}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? popoverId : undefined}
        onClick={() => (isOpen ? close() : open())}
        data-test-id={switcherTestId(switcher.kind, "trigger")}
      >
        <EntityIconSlot
          icon={icon}
          label={label}
          className={`${BLOCK}__icon`}
          logoSize="xs"
        />
        <span
          ref={labelRef}
          className={classNames(`${BLOCK}__label`, {
            "breadcrumb-last-item-label": isLast,
          })}
          title={isLabelTruncated ? label : undefined}
        >
          {label}
        </span>
        {isPrimary && !isOpen && (
          <kbd className={`${BLOCK}__kbd`} aria-hidden="true">
            /
          </kbd>
        )}
        <ChevronDown
          size={14}
          className={`${BLOCK}__chevron`}
          aria-hidden="true"
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className={`${BLOCK}-panel`}
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              // Hidden until measured, so it never paints at the origin first.
              visibility: position ? "visible" : "hidden",
            }}
          >
            <Suspense
              fallback={
                <div className="tlx-breadcrumb-switcher-popup tlx-breadcrumb-switcher-popup--pending">
                  {t("breadcrumbSwitcher.loading")}
                </div>
              }
            >
              <EntitySwitcherPopover
                // A different entity kind means different hooks; remount rather
                // than reorder them.
                key={switcher.kind}
                switcher={switcher}
                onClose={close}
                triggerRef={triggerRef}
                popoverId={popoverId}
              />
            </Suspense>
          </div>,
          document.body,
        )}
    </>
  );
};

export default BreadcrumbEntitySwitcher;
